var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, query as firestoreQuery, where, updateDoc, getFirestore, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
// Collection names
const SERVICE_REQUESTS_COLLECTION = 'service_requests';
const BIDS_COLLECTION = 'bids';
const QUESTIONS_COLLECTION = 'questions';
/**
 * Hook for service-request-related Firebase queries and mutations using React Query
 */
export const useServiceRequestQueries = () => {
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();
    const db = getFirestore();
    // Query for getting all service requests for a client
    const useServiceRequestsByClient = (clientId) => {
        return useQuery({
            queryKey: ['serviceRequests', clientId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    return [];
                const requestsRef = collection(db, SERVICE_REQUESTS_COLLECTION);
                const q = firestoreQuery(requestsRef, where('clientId', '==', clientId));
                const querySnapshot = yield getDocs(q);
                return querySnapshot.docs.map(doc => {
                    var _a, _b, _c, _d;
                    const data = doc.data();
                    return Object.assign(Object.assign({}, data), { id: doc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate(), preferredDate: (_c = data.preferredDate) === null || _c === void 0 ? void 0 : _c.toDate(), biddingEndDate: (_d = data.biddingEndDate) === null || _d === void 0 ? void 0 : _d.toDate() });
                });
            }),
            enabled: !!currentUser && !!clientId
        });
    };
    // Query for getting all pending service requests available for bidding
    const useAvailableServiceRequests = () => {
        return useQuery({
            queryKey: ['availableServiceRequests'],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    return [];
                const requestsRef = collection(db, SERVICE_REQUESTS_COLLECTION);
                const q = firestoreQuery(requestsRef, where('status', '==', 'active'));
                const querySnapshot = yield getDocs(q);
                return querySnapshot.docs.map(doc => {
                    var _a, _b, _c, _d;
                    const data = doc.data();
                    return Object.assign(Object.assign({}, data), { id: doc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate(), preferredDate: (_c = data.preferredDate) === null || _c === void 0 ? void 0 : _c.toDate(), biddingEndDate: (_d = data.biddingEndDate) === null || _d === void 0 ? void 0 : _d.toDate() });
                });
            }),
            enabled: !!currentUser
        });
    };
    // Query for getting a single service request
    const useServiceRequest = (requestId) => {
        return useQuery({
            queryKey: ['serviceRequest', requestId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                if (!requestId)
                    return null;
                const requestRef = doc(db, SERVICE_REQUESTS_COLLECTION, requestId);
                const requestDoc = yield getDoc(requestRef);
                if (!requestDoc.exists())
                    return null;
                const data = requestDoc.data();
                return Object.assign(Object.assign({}, data), { id: requestDoc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate(), preferredDate: (_c = data.preferredDate) === null || _c === void 0 ? void 0 : _c.toDate(), biddingEndDate: (_d = data.biddingEndDate) === null || _d === void 0 ? void 0 : _d.toDate() });
            }),
            enabled: !!requestId
        });
    };
    // Query for getting all bids from a vendor for service requests
    const useVendorBids = (vendorId) => {
        return useQuery({
            queryKey: ['vendorBids', vendorId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser || !vendorId)
                    return [];
                const bidsRef = collection(db, BIDS_COLLECTION);
                const q = firestoreQuery(bidsRef, where('vendorId', '==', vendorId));
                const querySnapshot = yield getDocs(q);
                return querySnapshot.docs.map(doc => {
                    var _a, _b;
                    const data = doc.data();
                    return Object.assign(Object.assign({}, data), { id: doc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate() });
                });
            }),
            enabled: !!currentUser && !!vendorId
        });
    };
    // Query for getting all bids for a specific service request
    const useBidsByRequest = (requestId) => {
        return useQuery({
            queryKey: ['requestBids', requestId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!requestId)
                    return [];
                const bidsRef = collection(db, BIDS_COLLECTION);
                const q = firestoreQuery(bidsRef, where('requestId', '==', requestId));
                const querySnapshot = yield getDocs(q);
                return querySnapshot.docs.map(doc => {
                    var _a, _b;
                    const data = doc.data();
                    return Object.assign(Object.assign({}, data), { id: doc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate() });
                });
            }),
            enabled: !!requestId
        });
    };
    // Create a new service request
    const useCreateServiceRequest = () => {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ clientId, formData }) {
                const db = getFirestore();
                const requestsCollection = collection(db, 'service_requests');
                // Prepare the service request document data
                const now = new Date();
                // Calculate bidding end date (2 days after creation)
                const biddingEndDate = new Date(now);
                biddingEndDate.setDate(now.getDate() + 2);
                const requestData = {
                    clientId,
                    city: formData.city,
                    state: formData.state,
                    address: formData.address,
                    zipCode: formData.zipCode,
                    serviceType: formData.serviceType || 'Residential',
                    projectTimeline: formData.projectTimeline,
                    serviceName: formData.serviceName,
                    serviceDetails: formData.serviceDetails,
                    status: 'active',
                    createdAt: now,
                    updatedAt: now,
                    biddingEndDate: biddingEndDate,
                    bidCount: 0 // Initialize bid count to zero
                };
                // Add preferred date if specified
                if (formData.projectTimeline === 'Preferred Date' && formData.preferredDate) {
                    requestData.preferredDate = formData.preferredDate;
                }
                // Create the service request document
                const requestRef = yield addDoc(requestsCollection, requestData);
                // Invalidate queries to refetch the updated data
                queryClient.invalidateQueries({ queryKey: ['serviceRequests', clientId] });
                return requestRef.id;
            })
        });
    };
    // Mutation for updating a service request
    const useUpdateServiceRequest = () => {
        return useMutation({
            mutationFn: (data) => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    throw new Error('User not authenticated');
                const { requestId, clientId, updates } = data;
                const requestRef = doc(db, SERVICE_REQUESTS_COLLECTION, requestId);
                yield updateDoc(requestRef, Object.assign(Object.assign({}, updates), { updatedAt: serverTimestamp() }));
                // Invalidate relevant queries
                queryClient.invalidateQueries({ queryKey: ['serviceRequests', clientId] });
                queryClient.invalidateQueries({ queryKey: ['serviceRequest', requestId] });
            })
        });
    };
    // Mutation for submitting a bid
    const useSubmitBid = () => {
        return useMutation({
            mutationFn: (bidData) => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    throw new Error('Must be authenticated to submit a bid');
                // Create the base bid object with type declaration
                const newBid = {
                    requestId: bidData.requestId,
                    vendorId: bidData.vendorId,
                    intent: bidData.intent,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };
                // Add fields based on intent
                if (bidData.intent === 'bid') {
                    if (!bidData.amount || bidData.amount <= 0) {
                        throw new Error('Amount is required for bids and must be greater than zero');
                    }
                    newBid.amount = bidData.amount;
                    newBid.notes = bidData.notes || '';
                    newBid.status = 'pending';
                    // Add timing details - now required
                    if (!bidData.earliestStartDate) {
                        throw new Error('Earliest start date is required for bids');
                    }
                    newBid.earliestStartDate = bidData.earliestStartDate;
                    if (!bidData.projectDuration || bidData.projectDuration <= 0) {
                        throw new Error('Project duration is required and must be greater than zero');
                    }
                    newBid.projectDuration = bidData.projectDuration;
                    // Add attachments if present
                    if (bidData.attachments && Array.isArray(bidData.attachments) && bidData.attachments.length > 0) {
                        newBid.attachments = bidData.attachments;
                    }
                    // Increment the bidCount on the service request
                    const requestRef = doc(db, SERVICE_REQUESTS_COLLECTION, bidData.requestId);
                    const requestSnap = yield getDoc(requestRef);
                    if (requestSnap.exists()) {
                        // Update bidCount using increment operation
                        const currentBidCount = requestSnap.data().bidCount || 0;
                        yield updateDoc(requestRef, {
                            bidCount: currentBidCount + 1,
                            updatedAt: serverTimestamp()
                        });
                    }
                }
                else if (bidData.intent === 'ignore') {
                    // For ignore intent, only add the reason field
                    newBid.reason = bidData.reason || 'No reason provided';
                }
                const bidRef = yield addDoc(collection(db, BIDS_COLLECTION), newBid);
                return Object.assign({ id: bidRef.id }, newBid);
            }),
            onSuccess: (_, variables) => {
                // Invalidate related queries
                queryClient.invalidateQueries({ queryKey: ['vendorBids'] });
                queryClient.invalidateQueries({ queryKey: ['requestBids'] });
                // Also invalidate service request queries since bidCount has changed
                queryClient.invalidateQueries({ queryKey: ['serviceRequest', variables.requestId] });
                queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
            },
        });
    };
    // Mutation for updating a bid status (only applies to bids with intent='bid')
    const useUpdateBidStatus = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ bidId, status }) {
                if (!currentUser || !bidId)
                    throw new Error('Must be authenticated to update a bid');
                // Get the bid to check its intent
                const bidRef = doc(db, BIDS_COLLECTION, bidId);
                const bidSnap = yield getDoc(bidRef);
                if (!bidSnap.exists()) {
                    throw new Error('Bid not found');
                }
                const bidData = bidSnap.data();
                // Only allow status updates for actual bids
                if (bidData.intent !== 'bid') {
                    throw new Error('Cannot update status for a declined request');
                }
                yield updateDoc(bidRef, {
                    status,
                    updatedAt: serverTimestamp()
                });
                return { bidId, status };
            }),
            onSuccess: (data) => {
                // Invalidate related queries
                queryClient.invalidateQueries({ queryKey: ['vendorBids'] });
                queryClient.invalidateQueries({ queryKey: ['requestBids', data.bidId] });
                // Add invalidation for serviceRequest queries to ensure UI updates
                queryClient.invalidateQueries({ queryKey: ['serviceRequest'] });
                queryClient.invalidateQueries({ queryKey: ['vendorServiceRequests'] });
                queryClient.invalidateQueries({ queryKey: ['availableServiceRequests'] });
            },
        });
    };
    // Mutation for closing a service request (with or without a winning bid)
    const useCloseServiceRequest = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ id, withWinner = true }) {
                if (!currentUser || !id)
                    throw new Error('Must be authenticated to close a request');
                const requestRef = doc(db, SERVICE_REQUESTS_COLLECTION, id);
                const requestSnap = yield getDoc(requestRef);
                if (!requestSnap.exists()) {
                    throw new Error('Service request not found');
                }
                // Use 'closed' status if there's a winner, otherwise use 'removed'
                const status = withWinner ? 'closed' : 'removed';
                yield updateDoc(requestRef, {
                    status,
                    closedAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                return { id, status };
            }),
            onSuccess: (data) => {
                // Invalidate related queries
                queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
                queryClient.invalidateQueries({ queryKey: ['serviceRequest', data.id] });
                // Add invalidation for additional queries to ensure UI updates
                queryClient.invalidateQueries({ queryKey: ['vendorBids'] });
                queryClient.invalidateQueries({ queryKey: ['requestBids'] });
                queryClient.invalidateQueries({ queryKey: ['vendorServiceRequests'] });
                queryClient.invalidateQueries({ queryKey: ['availableServiceRequests'] });
            },
        });
    };
    // Query for getting all service requests for which a vendor has submitted bids
    const useVendorServiceRequests = (vendorId) => {
        return useQuery({
            queryKey: ['vendorServiceRequests', vendorId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser || !vendorId)
                    return [];
                // First get all bids from this vendor
                const bidsRef = collection(db, BIDS_COLLECTION);
                const bidsQuery = firestoreQuery(bidsRef, where('vendorId', '==', vendorId), where('intent', '==', 'bid'));
                const bidsSnapshot = yield getDocs(bidsQuery);
                // If no bids, return empty array
                if (bidsSnapshot.empty)
                    return [];
                // Extract request IDs from bids
                const requestIds = Array.from(new Set(bidsSnapshot.docs.map(doc => doc.data().requestId)));
                // If no request IDs, return empty array
                if (requestIds.length === 0)
                    return [];
                // Get requests - we need to fetch in batches if there are many
                const MAX_BATCH_SIZE = 10; // Firestore 'in' queries are limited to 10 items
                let allRequests = [];
                for (let i = 0; i < requestIds.length; i += MAX_BATCH_SIZE) {
                    const batchIds = requestIds.slice(i, i + MAX_BATCH_SIZE);
                    const requestsRef = collection(db, SERVICE_REQUESTS_COLLECTION);
                    const requestsQuery = firestoreQuery(requestsRef, where('__name__', 'in', batchIds));
                    const requestsSnapshot = yield getDocs(requestsQuery);
                    const batchRequests = requestsSnapshot.docs.map(doc => {
                        var _a, _b, _c, _d;
                        const data = doc.data();
                        return Object.assign(Object.assign({}, data), { id: doc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate(), preferredDate: (_c = data.preferredDate) === null || _c === void 0 ? void 0 : _c.toDate(), biddingEndDate: (_d = data.biddingEndDate) === null || _d === void 0 ? void 0 : _d.toDate() });
                    });
                    allRequests = [...allRequests, ...batchRequests];
                }
                return allRequests;
            }),
            enabled: !!currentUser && !!vendorId
        });
    };
    // Query for getting questions for a service request
    const useQuestionsByRequest = (requestId) => {
        return useQuery({
            queryKey: ['questions', requestId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!requestId)
                    return [];
                const questionsRef = collection(db, QUESTIONS_COLLECTION);
                const q = firestoreQuery(questionsRef, where('requestId', '==', requestId));
                const querySnapshot = yield getDocs(q);
                return querySnapshot.docs.map(doc => {
                    var _a, _b, _c;
                    const data = doc.data();
                    return Object.assign(Object.assign({}, data), { id: doc.id, createdAt: (_a = data.createdAt) === null || _a === void 0 ? void 0 : _a.toDate(), updatedAt: (_b = data.updatedAt) === null || _b === void 0 ? void 0 : _b.toDate(), answeredAt: (_c = data.answeredAt) === null || _c === void 0 ? void 0 : _c.toDate() });
                });
            }),
            enabled: !!requestId
        });
    };
    // Mutation for asking a question on a service request
    const useAskQuestion = () => {
        return useMutation({
            mutationFn: (questionData) => __awaiter(void 0, void 0, void 0, function* () {
                const { requestId, text } = questionData;
                if (!requestId || !text.trim()) {
                    throw new Error('Request ID and question text are required');
                }
                // Check if the service request exists
                const requestRef = doc(db, SERVICE_REQUESTS_COLLECTION, requestId);
                const requestSnap = yield getDoc(requestRef);
                if (!requestSnap.exists()) {
                    throw new Error('Service request not found');
                }
                const now = new Date();
                const newQuestion = {
                    requestId,
                    text: text.trim(),
                    createdAt: now,
                    updatedAt: now
                };
                const questionRef = yield addDoc(collection(db, QUESTIONS_COLLECTION), newQuestion);
                return Object.assign({ id: questionRef.id }, newQuestion);
            }),
            onSuccess: (data) => {
                // Invalidate related queries
                queryClient.invalidateQueries({ queryKey: ['questions', data.requestId] });
            },
        });
    };
    // Mutation for answering a question (client only)
    const useAnswerQuestion = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ questionId, answer }) {
                if (!currentUser)
                    throw new Error('Must be authenticated to answer a question');
                if (!questionId || !answer.trim()) {
                    throw new Error('Question ID and answer text are required');
                }
                const questionRef = doc(db, QUESTIONS_COLLECTION, questionId);
                const questionSnap = yield getDoc(questionRef);
                if (!questionSnap.exists()) {
                    throw new Error('Question not found');
                }
                const questionData = questionSnap.data();
                const now = new Date();
                yield updateDoc(questionRef, {
                    answer: answer.trim(),
                    answeredAt: now,
                    updatedAt: now
                });
                return {
                    id: questionId,
                    requestId: questionData.requestId,
                    answer
                };
            }),
            onSuccess: (data) => {
                // Invalidate related queries
                queryClient.invalidateQueries({ queryKey: ['questions', data.requestId] });
            },
        });
    };
    return {
        useServiceRequestsByClient,
        useAvailableServiceRequests,
        useServiceRequest,
        useVendorBids,
        useBidsByRequest,
        useCreateServiceRequest,
        useUpdateServiceRequest,
        useSubmitBid,
        useUpdateBidStatus,
        useCloseServiceRequest,
        useVendorServiceRequests,
        useQuestionsByRequest,
        useAskQuestion,
        useAnswerQuestion
    };
};
